import { Box, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { stageColourFor, stageColourValue, tokens } from '../../theme';
import type { PhaseChipProps } from './types';

/**
 * A phase, as a pill in its stage's colour. Three states from the design's
 * component-states frame: **default** is the colour as text inside a hairline of
 * it, **hover** tints the fill, **active** fills solid with a white label.
 *
 * **Hover is a tint and not a solid fill**, because a filled chip is the one
 * state that must be unique on the rail — filling on hover makes every chip
 * momentarily look like the current page, and flips its label to white on a
 * pointer move.
 *
 * Focus is not styled here; the app has one ring, in the baseline. A chip with no
 * `to` is a label rather than a link, which nothing renders today.
 */

/**
 * The stage colour at 15% over the page, as the hover fill. **`color-mix` rather
 * than an alpha channel**: a translucent fill picks up whatever is behind it,
 * and the chip sits on the page in the rail and could sit on `surface.raised`
 * later. **The ground is a parameter and not the keyword `white`**, which would
 * be a light-mode assumption made outside the theme.
 */
const tintOf = (colour: string, ground: string): string =>
  `color-mix(in srgb, ${colour} 15%, ${ground})`;

export const PhaseChip = ({ label, stage, active = false, to }: PhaseChipProps) => {
  const theme = useTheme();

  // Neutral rather than a guessed colour: a grey chip is visibly odd, which is
  // the point.
  //
  // Two roles on one chip: the solid fill an active chip's white label sits on,
  // and the ink its border and inactive label are drawn in.
  const fill = stageColourFor(stage, 'fill');
  const ink = stageColourValue(theme, stage, 'onSurface');

  const chip = (
    <Box
      sx={{
        // Fills the width it is given rather than hugging its label, so a
        // stage's chips span its bar exactly and the rail reads as one band per
        // stage rather than pills floating under a heading.
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        height: 24,
        paddingInline: 3,
        borderRadius: `${String(tokens.radius.full)}px`,
        border: `1px solid ${ink}`,
        backgroundColor: active ? fill : 'transparent',
        transition: 'background-color 150ms ease-out, border-color 150ms ease-out',
        ...(to !== undefined && !active
          ? { '&:hover': { backgroundColor: tintOf(ink, theme.palette.surface.page) } }
          : {}),
      }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{
          fontFamily: tokens.fontFamily.heading,
          fontWeight: 600,
          lineHeight: 1,
          // A short title is two or three words and the chip is sized for it:
          // the genuinely-fixed-content case `nowrap` is for.
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          color: active ? 'text.onBrand' : ink,
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (to === undefined) {
    return chip;
  }

  return (
    <RouterLink
      to={to}
      style={{ textDecoration: 'none', display: 'flex', flex: 1, minWidth: 0 }}
      // What turns eleven links into a position indicator.
      aria-current={active ? 'page' : undefined}
    >
      {chip}
    </RouterLink>
  );
};
